import { useMemo } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { infoButtonBaseClasses, tabClasses, Tabs } from '@kc-console/common-ui';
import {
  useHandleScrollToEntry,
  useRatioIntersectingEntry,
  useScrollObserverRootContext,
} from '@kc-console/context';

import { DETAIL_VIEW_SCROLL_OFFSET_TOP } from './constants';

import type { DetailViewTabsProps } from './types';

/**
 * [ DetailView의 props.sections을 받아 앵커 기반 Tabs를 랜더링하는 컴포넌트 ]
 *
 * - DetailView에서 주어진 sections 배열로 Tabs를 구성하여 랜더링하는 컴포넌트입니다.
 * - DetailLayout 하위 DetailSection 컴포넌트 내부 스크롤 시 위치에 맞는 Tab은 Activated됩니다.
 * - 각 탭 클릭 시 연동된 Section으로 스크롤을 이동시키는 기능을 제공합니다.
 * - 서비스 개발 시 직접 사용되지 않으며 DetailView 내부에서 사용됩니다.
 */
const DetailViewTabs = ({ sections }: DetailViewTabsProps) => {
  const { rootId } = useScrollObserverRootContext();

  const ratioIntersectingEntry = useRatioIntersectingEntry({ rootId });
  const currentEntryId = useMemo(() => {
    return ratioIntersectingEntry || sections[0].entryId;
  }, [ratioIntersectingEntry, sections]);

  const handleScrollToEntry = useHandleScrollToEntry({
    entryIds: sections.map(({ entryId }) => entryId),
    rootId,
    offsetTop: DETAIL_VIEW_SCROLL_OFFSET_TOP,
  });

  return (
    <StyledWrapper>
      <StyledTabs
        value={currentEntryId}
        onChange={value => {
          handleScrollToEntry(value);
        }}
        items={sections.map(section => ({
          label: section.header,
          endAdornment: section.headerEndAdornment,
          value: section.entryId,
          onClick: (_, value) => handleScrollToEntry(value),
        }))}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')`
  display: flex;
  background-color: ${({ theme }) => theme.color['background/default']};
  z-index: ${({ theme }) => theme.zIndex.sticky};
  padding-top: 4px;
  margin-bottom: 12px;
  top: 0px;
  position: sticky;
`;

const StyledTabs = styled(Tabs)(
  ({ theme }) => css`
    flex: 1;
    /**
     * 포커스 링의 가로 방향이 탭 요소 밖으로 나가므로, scroller(overflow-x: auto)의
     * 왼쪽 경계에 닿는 첫 번째 탭에서 링이 잘린다. 탭 목록에 왼쪽 여유를 주고 같은
     * 크기만큼 당겨, 레이아웃 위치는 그대로 두고 클리핑 여유만 확보한다.
     */
    margin-left: -4px;
    .MuiTabs-flexContainer {
      padding-left: 4px;
    }
    &::after {
      z-index: -1;
    }
    .${tabClasses.item} {
      padding-left: 0;
      padding-right: 0;
    }
    .${tabClasses.item}:not(last-of-type) {
      margin-right: 24px;
    }
    .${tabClasses.item}:not(.Mui-selected, .Mui-disabled:has(.${infoButtonBaseClasses.root}:hover)):hover {
      background-color: initial;
    }
    /**
     * 공통 Tab은 selected 좌우 세로 보더와 hover 하단선을 ::before 로 그리지만,
     * 앵커 탭은 밑줄형이라 두 장식을 감춘다. (KIC-36462)
     * 다만 공통 Tab은 포커스 링도 같은 ::before 에 그리므로(focusRing('inside') 기본값),
     * :focus-visible 만 제외해 키보드 포커스 표시가 남도록 한다. (KIC-63347)
     */
    .${tabClasses.item}:not(:focus-visible)::before {
      display: none;
    }
    /**
     * MUI Tab root 는 overflow: hidden 이라 요소 밖으로 나간 ::before 가 잘린다.
     * 가로 방향 포커스 링이 보이도록 포커스 상태에서만 클리핑을 푼다. 공통 Tab 이
     * max-width: initial 로 MUI 의 maxWidth 제한을 이미 해제했으므로 라벨이 잘릴 일은 없다.
     */
    .${tabClasses.item}:focus-visible {
      overflow: visible;
    }
    /**
     * :focus-visible 에서 ::before 가 살아나면 공통 Tab 의 selected(top: 0 · height: 100%)와
     * hover(inset 0 · height: 1px · border-bottom) 선언이 포커스 링과 같은 레이어에 남아
     * 링 형태가 깨진다. hover 규칙보다 우선순위가 높도록 :not(.Mui-disabled) 를 더해
     * focusRing('inside') 이 그리는 사각형만 남긴다.
     * 가로까지 inside 로 두면 앵커 탭은 좌우 padding 이 0 이라 안쪽 링이 라벨 텍스트를
     * 덮으므로, 세로만 inside(2px) 로 유지하고 가로는 바깥(-2px)으로 내보낸다.
     */
    .${tabClasses.item}:focus-visible:not(.Mui-disabled)::before {
      inset: 2px -2px; /* 세로: focusRing('inside') 기본 offset · 가로: outside */
      height: auto;
      border: 2px solid ${theme.color['focusRing/inverse']}; /* focusRing 안쪽 링 색 */
    }
    .${tabClasses.item}.Mui-selected::after {
      top: initial;
      bottom: 0px;
    }
    .${tabClasses.item}:not(.Mui-selected, .Mui-disabled:has(.${infoButtonBaseClasses.root}:hover)):hover::after {
      top: initial;
      bottom: 0px;
      border-bottom-color: ${theme.color['border/strong/default']};
    }
  `,
);

export default DetailViewTabs;
